import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Calculator, ArrowRight, ClipboardList, Book } from 'lucide-react';
import { accountingAPI } from '@/lib/api';

function money(v) {
  const n = Number(v || 0);
  return `${n < 0 ? '-$' : '$'}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
}

const ADJ_TYPE_LABEL = {
  manual_payment: 'Payment', manual_credit: 'Credit',
  manual_debit: 'Debit', manual_recovery: 'Recovery',
};

const ADJ_STATUS_TONE = {
  draft: 'bg-slate-100 text-slate-700 border-slate-300',
  pending_approval: 'bg-amber-100 text-amber-800 border-amber-300',
  posted: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  cancelled: 'bg-neutral-100 text-neutral-700 border-neutral-300',
};

/**
 * Compact "Accounting" section for Agent / Agency profile pages.
 * Shows the recipient's snapshot (balance / recovery), recent manual
 * adjustments, and quick links to the full Ledger Explorer / History
 * filtered by this recipient.
 */
export default function RecipientAccountingSection({ recipientType, recipientId }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!recipientType || !recipientId) return;
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        accountingAPI.recipientAccountingSummary(recipientType, recipientId),
        accountingAPI.listAdjustments({ recipient_type: recipientType, recipient_id: recipientId, limit: 10 }),
      ]);
      setSummary(s.data);
      setAdjustments(a.data.items || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [recipientType, recipientId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="recipient-accounting-section">
      <CardHeader className="pb-3 flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Calculator className="h-4 w-4" /> Accounting
        </CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate(`/accounting/ledger?recipient_type=${recipientType}&recipient_id=${recipientId}`)} data-testid="open-ledger-btn">
            <Book className="h-4 w-4 mr-1.5" /> Ledger
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate(`/accounting/history?recipient_type=${recipientType}&recipient_id=${recipientId}`)}>
            <ArrowRight className="h-4 w-4 mr-1.5" /> History
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="p-6 text-slate-500 flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !summary ? (
          <div className="p-4 text-slate-500 text-sm">No accounting data yet.</div>
        ) : (
          <>
            {/* Summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Tile
                label="Outstanding Balance"
                value={money(summary.outstanding_balance)}
                tone={summary.outstanding_balance >= 0 ? 'emerald' : 'red'}
              />
              <Tile
                label="Outstanding Payable"
                value={money(summary.outstanding_positive)}
                tone="emerald"
              />
              <Tile
                label="Outstanding Recovery"
                value={money(summary.outstanding_recovery)}
                tone="red"
              />
              <Tile
                label="Statements"
                value={`${summary.statements_open} open / ${summary.statements_total}`}
                tone="slate"
              />
            </div>

            {/* Manual adjustments mini-table */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">Recent Manual Adjustments</div>
                <Button size="sm" variant="ghost" onClick={() => navigate('/accounting/manual-adjustments')}>
                  <ClipboardList className="h-3.5 w-3.5 mr-1" /> All
                </Button>
              </div>
              {adjustments.length === 0 ? (
                <div className="text-xs text-slate-500 italic py-2">No manual adjustments.</div>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="text-left px-2 py-1.5 font-medium">Date</th>
                        <th className="text-left px-2 py-1.5 font-medium">Type</th>
                        <th className="text-right px-2 py-1.5 font-medium">Amount</th>
                        <th className="text-left px-2 py-1.5 font-medium">Reason</th>
                        <th className="text-left px-2 py-1.5 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adjustments.slice(0, 6).map((a) => (
                        <tr key={a.adjustment_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                            onClick={() => navigate('/accounting/manual-adjustments')}>
                          <td className="px-2 py-1.5">{fmtDate(a.created_at)}</td>
                          <td className="px-2 py-1.5">{ADJ_TYPE_LABEL[a.adjustment_type]}</td>
                          <td className="px-2 py-1.5 text-right font-medium">{money(a.amount)}</td>
                          <td className="px-2 py-1.5">{a.reason}</td>
                          <td className="px-2 py-1.5">
                            <Badge className={`${ADJ_STATUS_TONE[a.status] || 'bg-slate-100 text-slate-700 border-slate-300'} border font-normal text-[10px]`}>
                              {a.status.replace('_', ' ')}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Tile({ label, value, tone = 'slate' }) {
  const toneMap = {
    emerald: 'text-emerald-800 bg-emerald-50 border-emerald-200',
    red:     'text-red-800 bg-red-50 border-red-200',
    slate:   'text-slate-800 bg-slate-50 border-slate-200',
  };
  return (
    <div className={`rounded-md border p-2.5 ${toneMap[tone]}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      <div className="text-base font-medium">{value}</div>
    </div>
  );
}
